import { inArray } from 'drizzle-orm'

import { user } from '@package/drizzle/schema'

import type { DatabaseOrTransaction } from './types'

/**
 * Child-to-child notifications are structurally supported but disabled until a
 * moderation story (report/block) exists. Flip this to enable; no migration needed.
 */
export const NOTIFY_CHILD_TO_CHILD_ENABLED = false

interface CanNotifyParams {
  actorId: string
  recipientIds: string[]
  type: string
}

/**
 * Returns true only if the actor may notify every recipient.
 *
 * `userType === 'main'` marks an adult account; anything else is a child.
 */
export async function canNotify(
  db: DatabaseOrTransaction,
  { actorId, recipientIds }: CanNotifyParams,
): Promise<boolean> {
  if (recipientIds.length === 0) return false

  const ids = [...new Set([actorId, ...recipientIds])]
  const rows = await db.query.user.findMany({
    where: inArray(user.id, ids),
    columns: { id: true, userType: true },
  })

  const byId = new Map(rows.map((row) => [row.id, row.userType]))
  const actorType = byId.get(actorId)
  if (!actorType) return false

  const actorIsAdult = actorType === 'main'

  return recipientIds.every((recipientId) => {
    const recipientType = byId.get(recipientId)
    if (!recipientType) return false

    const recipientIsAdult = recipientType === 'main'

    // adult -> adult, adult -> child, child -> adult are all permitted.
    if (actorIsAdult || recipientIsAdult) return true

    // child -> child is gated.
    return NOTIFY_CHILD_TO_CHILD_ENABLED
  })
}
